import { useEffect, useMemo, useState } from "react"

import { Permission, Role } from "@/types/role"
import removePermissionFromRole from "@/services/UserService/removePermissionFromRole";
import assignPermissionToRole from "@/services/UserService/assignPermissionToRole";
import getRolePermissions from "@/services/UserService/getRolePermissions";
import getPermissions from "@/services/UserService/getPermissions";
import Splash from "@/components/splash/Splash";
import Checkbox from "@/components/UiElements/Checkbox";
import {closeNotif, startNotif} from "@/components/DynamicForm/util/notifier";
import {getErrorMessage} from "@/utils/errorMessages";


type RolePermissionsListProps = {
    role: Role
}

type PermissionGroup = {
    key: string
    object: string
    service: string
    domain: string
    permissions: Permission[]
}

const formatPermissionLabel = (value: string) => (
    value
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, character => character.toUpperCase())
)

const RolePermissionsList = ({role}: RolePermissionsListProps) => {
    const [rolePermissions, setRolePermissions] = useState<Permission[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updatingPermissionIds, setUpdatingPermissionIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        let cancelled = false

        const loadPermissions = async () => {
            setIsLoading(true)
            setUpdatingPermissionIds(new Set())

            const [rolePermissionsResult, permissionsResult] = await Promise.all([
                getRolePermissions(role.name),
                getPermissions(),
            ])

            if (cancelled) return

            setRolePermissions(
                Array.isArray(rolePermissionsResult)
                    ? rolePermissionsResult
                    : rolePermissionsResult?.permissions || []
            )
            setPermissions(
                Array.isArray(permissionsResult)
                    ? permissionsResult
                    : permissionsResult?.permissions || []
            )
            setIsLoading(false)
        }

        loadPermissions()

        return () => {
            cancelled = true
        }
    }, [role.id]);

    const assignedPermissionIds = useMemo(
        () => new Set(rolePermissions.map(permission => permission.id)),
        [rolePermissions]
    )

    const permissionGroups = useMemo<PermissionGroup[]>(() => {
        const groups = new Map<string, PermissionGroup>()

        permissions.forEach(permission => {
            const key = `${permission.service}::${permission.domain}::${permission.object}`
            const group = groups.get(key)

            if (group) {
                group.permissions.push(permission)
                return
            }

            groups.set(key, {
                key,
                object: permission.object,
                service: permission.service,
                domain: permission.domain,
                permissions: [permission],
            })
        })

        return Array.from(groups.values())
            .map(group => ({
                ...group,
                permissions: [...group.permissions].sort((a, b) => a.action.localeCompare(b.action)),
            }))
            .sort((a, b) => {
                const objectComparison = a.object.localeCompare(b.object)
                if (objectComparison !== 0) return objectComparison

                return `${a.service}-${a.domain}`.localeCompare(`${b.service}-${b.domain}`)
            })
    }, [permissions])

    const handlePermissionToggle = async (permission: Permission, checked: boolean) => {
        if (updatingPermissionIds.has(permission.id)) return

        const wasAssigned = assignedPermissionIds.has(permission.id)
        if (wasAssigned === checked) return

        setUpdatingPermissionIds(current => new Set(current).add(permission.id))
        setRolePermissions(current => checked
            ? (current.some(item => item.id === permission.id) ? current : [...current, permission])
            : current.filter(item => item.id !== permission.id)
        )
        const notificationId = startNotif('Saving permission...')

        const rollback = () => {
            setRolePermissions(current => wasAssigned
                ? (current.some(item => item.id === permission.id) ? current : [...current, permission])
                : current.filter(item => item.id !== permission.id)
            )
        }

        try {
            if (checked) {
                await assignPermissionToRole(role.id, [permission.id])
            } else {
                await removePermissionFromRole(role.id, permission.id)
            }
            closeNotif(notificationId, 'Permission saved successfully.')
        } catch (error) {
            rollback()
            closeNotif(
                notificationId,
                getErrorMessage(error, 'Failed to save permission.'),
                {type: 'error'},
            )
        } finally {
            setUpdatingPermissionIds(current => {
                const next = new Set(current)
                next.delete(permission.id)
                return next
            })
        }
    }

    if (isLoading) {
        return <Splash text="Loading permissions..." />
    }

    if (permissionGroups.length === 0) {
        return (
            <div className="flex min-h-[100px] items-center justify-center rounded-md bg-gray-50 px-md py-xl text-text-md text-gray-400">
                No permissions are available.
            </div>
        )
    }

    return (
        <ul className="divide-y divide-gray-200">
            {permissionGroups.map(group => (
                <li
                    key={group.key}
                    className="flex min-h-[76px] w-full flex-row items-center justify-between gap-xl px-md py-md transition-colors hover:bg-gray-50"
                >
                    <div className="min-w-0">
                        <p className="truncate text-title-md text-gray-900">
                            {formatPermissionLabel(group.object)}
                        </p>
                        <p className="mt-xs truncate text-text-sm text-gray-400">
                            {`${group.service} - ${group.domain}`}
                        </p>
                    </div>

                    <div className="flex flex-row flex-wrap justify-end gap-sm">
                        {group.permissions.map(permission => {
                            const isAssigned = assignedPermissionIds.has(permission.id)
                            const isUpdating = updatingPermissionIds.has(permission.id)

                            return (
                                <Checkbox
                                    key={permission.id}
                                    label={formatPermissionLabel(permission.action)}
                                    defaultChecked={isAssigned}
                                    disabled={isUpdating}
                                    onChange={checked => handlePermissionToggle(permission, checked)}
                                    className={checked => (
                                        `min-h-[37px] rounded-md border px-sm transition-all ${
                                            checked
                                                ? 'border-primary-200 bg-primary-50'
                                                : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                                        } ${isUpdating ? 'opacity-50' : ''}`
                                    )}
                                />
                            )
                        })}
                    </div>
                </li>
            ))}
        </ul>
    )
}

export default RolePermissionsList
